<template>
  <div class="system-monitor-container">
    <!-- 顶部导航菜单 -->
    <div class="top-nav">
      <el-menu
        :default-active="activeNav"
        mode="horizontal"
        @select="handleNavSelect"
        class="nav-menu"
      >
        <el-menu-item index="monitor">{{ $t("message.ClientProjectMonitor.SystemMonitoring") }}</el-menu-item>        
        <el-menu-item v-if="false" index="setting">{{ $t("message.ClientProjectMonitor.systemSettings") }}</el-menu-item>
        <el-menu-item v-if="false" index="debug">{{ $t("message.ClientProjectMonitor.systemDebug") }}</el-menu-item>
        <el-menu-item v-if="false" index="energy">{{ $t("message.ClientProjectMonitor.energyAnalysis") }}</el-menu-item>
        <el-menu-item v-if="false" index="reports">{{ $t("message.ClientProjectMonitor.historyReports") }}</el-menu-item>
        <el-menu-item v-if="false" index="events">{{ $t("message.ClientProjectMonitor.eventManagement") }}</el-menu-item>
      </el-menu>
      
      <!-- 导航操作区域 - 确保在同一行 -->
      <div class="nav-actions">
        <!-- 动态单选框组 - 添加滚动功能 -->
        <div class="radio-group-wrapper">
          <!-- 左滚动按钮 -->
          <el-button 
            v-if="showScrollButtons"
            @click="scrollLeft"
            class="scroll-btn scroll-left"
            size="small"
            circle
          >
            <el-icon><ArrowLeft /></el-icon>
          </el-button>
          
          <div class="radio-group-container" ref="radioContainer">
            <el-radio-group 
              v-model="systemBussType" 
              @change="handleSaveLoginTypeChange"
              size="default"
              class="business-radio-group"
            >
              <el-radio 
                v-for="option in CollectionsOptions" 
                :key="option.value"
                :label="option.value"
                :class="{ 'active-radio': systemBussType === option.value }"
              >
                {{ $t(option.label) }}
              </el-radio>
            </el-radio-group>
          </div>
          
          <!-- 右滚动按钮 -->
          <el-button 
            v-if="showScrollButtons"
            @click="scrollRight"
            class="scroll-btn scroll-right"
            size="small"
            circle
          >
            <el-icon><ArrowRight /></el-icon>
          </el-button>
        </div>
        
        <!-- 间隔 -->
        <div class="action-spacer"></div>
        
        <!-- 全屏按钮 -->
        <div class="fullscreen-wrapper">
          <el-tooltip :content="isFullscreen ? $t('message.ClientProjectMonitor.exitFullscreen') : $t('message.ClientProjectMonitor.enterFullscreen')" placement="bottom">
            <el-button 
              @click="toggleFullscreen"
              class="fullscreen-btn"
              :class="{ 'fullscreen-active': isFullscreen }"
              size="default"
              plain
            >
              <el-icon v-if="isFullscreen"><Close /></el-icon>
              <el-icon v-else><FullScreen /></el-icon>
            </el-button>
          </el-tooltip>
        </div>
      </div>
    </div>

    <!-- 主要内容区域 -->
    <div class="main-content">
      <!-- 页面内容保持不变 -->
      <div v-if="activeNav === 'monitor'" class="page-content">
        <SystemMonitor 
          :businessId="currentBusinessId"
          :projectId="currentProjectId"
          :collectionId = "systemBussType"
          :domainMenuId="1"          
          @back="switchView('list')"
        />            
      </div>
      <div v-if="activeNav === 'setting'" class="page-content">
        <SystemSettings 
          :businessId="currentBusinessId"
          :projectId="currentProjectId"
          :collectionId = "systemBussType"
          :domainMenuId="2"          
          @back="switchView('list')"
        />
      </div>
      <div v-if="activeNav === 'debug'" class="page-content">
        <SystemDebug 
          :businessId="currentBusinessId"
          :projectId="currentProjectId"
          :collectionId = "systemBussType"
          :domainMenuId="3"          
          @back="switchView('list')"
        />            
      </div>
      <!-- 其他页面内容... -->
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, nextTick, watch, defineProps } from 'vue'
import { ElMessage } from 'element-plus'
import { FullScreen, Close, ArrowLeft, ArrowRight } from '@element-plus/icons-vue'
import SystemMonitor from "@/components/clientViews/ClientDeviceMonitorSystemMonitor.vue";
import SystemSettings from "@/components/clientViews/ClientDeviceMonitorSystemSettings.vue";
import SystemDebug from "@/components/clientViews/ClientDeviceMonitorSystemDebug.vue";
import { useI18n } from "vue-i18n";
import {projectCollections} from "@/api/ProjectManger";
const { t } = useI18n();

// 定义 props
const props = defineProps({
  projectId: {
    type: [String, Number],
    required: true
  },
  businessId: {
    type: [String, Number],
    required: true
  },
  projectName: {
    type: String,
    default: ''
  }
})

// 使用 props 数据
const currentProjectId = ref(props.projectId)
const currentBusinessId = ref(props.businessId)
const systemBussType = ref(0)
// 导航菜单
const activeNav = ref('monitor')


// 动态业务选项 - 使用国际化key
const CollectionsOptions = ref([])

// 全屏状态
const isFullscreen = ref(false)

// 滚动相关
const radioContainer = ref(null)
const showScrollButtons = ref(false)

// 监听 props 变化
// 正确写法：监听数组形式的目标，回调参数是新值数组
watch(
  // 第一个参数：监听的目标（数组，对应两个props）
  [() => props.projectId, () => props.businessId],
  // 第二个参数：回调函数（newVals 是数组，[新projectId, 新businessId]）
  (newVals) => {
    // 解构新值数组，赋值给响应式变量
    const [newProjectId, newBusinessId] = newVals;
    currentProjectId.value = newProjectId;
    currentBusinessId.value = newBusinessId;
    
    // 可选：解构旧值（oldVals 是 [旧projectId, 旧businessId]）
    // const [oldProjectId, oldBusinessId] = oldVals;
  },
  // 可选第三个参数：配置项（如立即执行、深度监听）
  { immediate: true, deep: false }
);

// 检查是否需要显示滚动按钮
const checkScrollNeeded = () => {
  nextTick(() => {
    if (radioContainer.value) {
      const container = radioContainer.value
      const radioGroup = container.querySelector('.business-radio-group')
      if (radioGroup) {
        const groupWidth = radioGroup.scrollWidth
        const containerWidth = container.clientWidth
        showScrollButtons.value = groupWidth > containerWidth
      }
    }
  })
}

// 左滚动
const scrollLeft = () => {
  if (radioContainer.value) {
    radioContainer.value.scrollBy({
      left: -150,
      behavior: 'smooth'
    })
  }
}

// 右滚动
const scrollRight = () => {
  if (radioContainer.value) {
    radioContainer.value.scrollBy({
      left: 150,
      behavior: 'smooth'
    })
  }
}

// 监听窗口大小变化
const handleResize = () => {
  checkScrollNeeded()
}

// 登录类型变化处理
const handleSaveLoginTypeChange = (value) => {  
  console.log('业务类型变化:', value)  
  systemBussType.value = value
  const option = CollectionsOptions.value.find(opt => opt.value === value);
  ElMessage.success(t('message.ClientProjectMonitor.switchedToMode', { mode: option.label }))
  nextTick(() => {
    console.log('当前业务类型:', systemBussType.value)
  })
}

// 获取业务类型标签
const LoadBusinessLabel = async () => {
  try {
    const params = {
      
      projectId: Number(currentProjectId.value),      
      businessId: Number(currentBusinessId.value),
      companyId: undefined,
      name: undefined,
      pageNum: 1,
      pageSize: 1000,
    };

    const res = await projectCollections(params);
    if(!res.success) {
      ElMessage.error(res?.msg);  
      return;
    }
    
    // 检查是否有数据
    if (!res.data.list || res.data.list.length === 0) {
      CollectionsOptions.value = [];
      systemBussType.value = 0;
      ElMessage.warning(t('message.ClientProjectMonitor.noBusinessData'));
      return;
    }
    
    CollectionsOptions.value = res.data.list.map(item => ({
      value: item.id.toString(), // 确保是字符串类型
      label: item.name
    }));   
    
    // 默认选中第一个选项
    systemBussType.value = CollectionsOptions.value[0].value;    
    console.log('业务选项加载成功，默认选中:', systemBussType.value);    
  } catch (error) {
    console.error('加载业务选项失败:', error);
    ElMessage.error(t('message.ClientProjectMonitor.loadBusinessFailed'));
  } 
}

// 切换全屏
const toggleFullscreen = () => {
  if (!document.fullscreenElement) {
    document.documentElement.requestFullscreen().then(() => {
      isFullscreen.value = true
      ElMessage.success(t('message.ClientProjectMonitor.enteredFullscreen'))
    }).catch(err => {
      console.error('全屏请求失败:', err)
      ElMessage.error(t('message.ClientProjectMonitor.fullscreenFailed'))
    })
  } else {
    if (document.exitFullscreen) {
      document.exitFullscreen().then(() => {
        isFullscreen.value = false
        ElMessage.info(t('message.ClientProjectMonitor.exitedFullscreen'))
      })
    }
  }
}

// 监听全屏状态变化
const handleFullscreenChange = () => {
  isFullscreen.value = !!document.fullscreenElement
}

// 监听ESC键退出全屏
const handleKeydown = (event) => {
  if (event.key === 'Escape' && isFullscreen.value) {
    isFullscreen.value = false
  }
}

// 导航选择处理
const handleNavSelect = (index) => {
  activeNav.value = index
 // ElMessage.info(t('message.ClientProjectMonitor.switchedToPage', { page: getNavName(index) }))
}



// 生命周期
onMounted(async() => {
    document.addEventListener('fullscreenchange', handleFullscreenChange)
    document.addEventListener('keydown', handleKeydown)
    window.addEventListener('resize', handleResize)
    LoadBusinessLabel();
    // 初始化检查滚动
    checkScrollNeeded()

    console.log("二层业务值:",currentBusinessId.value) 
})

onUnmounted(() => {
    if (isFullscreen.value && document.exitFullscreen) {
      document.exitFullscreen().then(() => {
        isFullscreen.value = false     
      })
    }
    document.removeEventListener('fullscreenchange', handleFullscreenChange)
    document.removeEventListener('keydown', handleKeydown)
    window.removeEventListener('resize', handleResize)
})

watch(systemBussType, (newVal, oldVal) => {
  console.log('🔍 systemBussType 变化:', {
    旧值: oldVal,
    新值: newVal,
    类型: typeof newVal
  })
  
  // 强制更新，确保子组件能接收到新值
  nextTick(() => {
    console.log('✅ 下一个 tick,当前值:', systemBussType.value)
  })
})

// 监听业务选项变化
watch(CollectionsOptions, () => {
  checkScrollNeeded()
}, { deep: true })

// 监听语言变化，重新检查滚动
watch(() => t(''), () => {
  nextTick(() => {
    checkScrollNeeded()
  })
})
</script>

<style scoped>
/* 样式保持不变，与之前相同 */
.system-monitor-container {
  padding: 0;
  background: #f5f7fa;  
  height: 100%;
  padding: 0px;
  overflow: hidden;
  box-sizing: border-box;
}

.top-nav {
  background: white;
  border-bottom: 1px solid #e6e6e6;
  margin-bottom: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 20px;
  height: 60px;
  min-height: 60px;
  flex-shrink: 0;
}

.nav-menu {
  background: white;
  border-bottom: none;
  flex: 1;
}

.nav-menu :deep(.el-menu-item) {
  font-size: 16px;
  font-weight: 500;
  height: 60px;
  line-height: 60px;
}

.nav-actions {
  display: flex;
  align-items: center;
  gap: 0;
  height: 100%;
  flex-shrink: 0;
}

.radio-group-wrapper {
  display: flex;
  align-items: center;
  height: 100%;
  gap: 8px;
}

.radio-group-container {
  max-width: 400px;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;
  -ms-overflow-style: none;
  white-space: nowrap;
}

.radio-group-container::-webkit-scrollbar {
  display: none;
}

.business-radio-group {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 100%;
  flex-wrap: nowrap;
}

.scroll-btn {
  width: 24px;
  height: 24px;
  padding: 0;
  border: 1px solid #dcdfe6;
  background: white;
  color: #606266;
  flex-shrink: 0;
}

.scroll-btn:hover {
  border-color: #409eff;
  color: #409eff;
  background-color: #f5f7fa;
}

.scroll-btn .el-icon {
  font-size: 12px;
}

:deep(.business-radio-group .el-radio) {
  margin-right: 0;
  padding: 8px 12px;
  border-radius: 4px;
  transition: all 0.3s ease;
  border: 1px solid transparent;
  height: 36px;
  display: flex;
  align-items: center;
  flex-shrink: 0;
  white-space: nowrap;
}

:deep(.business-radio-group .el-radio:hover) {
  background-color: #f5f7fa;
  border-color: #e4e7ed;
}

:deep(.business-radio-group .active-radio) {
  background-color: #ecf5ff;
  border-color: #409eff;
  color: #409eff;
}

:deep(.business-radio-group .el-radio__input.is-checked .el-radio__inner) {
  background-color: #409eff;
  border-color: #409eff;
}

:deep(.business-radio-group .el-radio__input.is-checked + .el-radio__label) {
  color: #409eff;
  font-weight: 500;
}

.action-spacer {
  width: 20px;
  height: 1px;
  flex-shrink: 0;
}

.fullscreen-wrapper {
  display: flex;
  align-items: center;
  height: 100%;
}

.fullscreen-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border-radius: 4px;
  transition: all 0.3s ease;
}

:deep(.fullscreen-btn) {
  border: 1px solid #dcdfe6;
  background: white;
  color: #606266;
}

:deep(.fullscreen-btn:hover) {
  border-color: #409eff;
  color: #409eff;
  background-color: #f5f7fa;
}

:deep(.fullscreen-btn.fullscreen-active) {
  border-color: #409eff;
  background-color: #409eff;
  color: white;
}

:deep(.fullscreen-btn.fullscreen-active:hover) {
  background-color: #3375b9;
  border-color: #3375b9;
  color: white;
}

:deep(.fullscreen-btn .el-icon) {
  font-size: 18px;
  font-weight: bold;
}

.main-content {
  height: calc(100% - 60px);
  overflow: hidden;
}

.page-content {
  height: 100%;
  overflow: hidden;
}

/* 响应式设计 */
@media (max-width: 1200px) {
  .top-nav {
    padding: 0 15px;
  }
  
  .radio-group-container {
    max-width: 350px;
  }
  
  .business-radio-group {
    gap: 4px;
  }
  
  :deep(.business-radio-group .el-radio) {
    padding: 6px 8px;
    font-size: 13px;
  }
  
  .action-spacer {
    width: 15px;
  }
}

@media (max-width: 992px) {
  .nav-menu :deep(.el-menu-item) {
    font-size: 14px;
    padding: 0 12px;
  }
  
  .radio-group-container {
    max-width: 300px;
  }
  
  :deep(.business-radio-group .el-radio) {
    padding: 4px 6px;
    font-size: 12px;
  }
  
  .business-radio-group {
    gap: 2px;
  }
}

@media (max-width: 768px) {
  .top-nav {
    padding: 0 10px;
    flex-wrap: nowrap;
  }
  
  .nav-menu {
    flex: 0 1 auto;
    overflow-x: auto;
  }
  
  .nav-menu :deep(.el-menu-item) {
    font-size: 13px;
    padding: 0 8px;
    white-space: nowrap;
  }
  
  .nav-actions {
    flex-shrink: 0;
  }
  
  .radio-group-container {
    max-width: 200px;
  }
  
  :deep(.business-radio-group .el-radio) {
    padding: 3px 4px;
    font-size: 11px;
  }
  
  .action-spacer {
    width: 8px;
  }
  
  .fullscreen-btn {
    width: 32px;
    height: 32px;
  }
  
  :deep(.fullscreen-btn .el-icon) {
    font-size: 16px;
  }
  
  .scroll-btn {
    width: 20px;
    height: 20px;
  }
}

@media (max-width: 576px) {
  .top-nav {
    padding: 0 5px;
  }
  
  .nav-menu :deep(.el-menu-item) {
    font-size: 12px;
    padding: 0 6px;
  }
  
  .radio-group-container {
    max-width: 150px;
  }
  
  .business-radio-group {
    gap: 1px;
  }
  
  :deep(.business-radio-group .el-radio) {
    padding: 2px 3px;
    font-size: 10px;
  }
  
  .action-spacer {
    width: 5px;
  }
  
  .fullscreen-btn {
    width: 30px;
    height: 30px;
  }
  
  :deep(.fullscreen-btn .el-icon) {
    font-size: 14px;
  }
  
  .scroll-btn {
    width: 18px;
    height: 18px;
  }
  
  .scroll-btn .el-icon {
    font-size: 10px;
  }
}
</style>